import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { 
  Shield, Cloud, Users, TestTube2, CreditCard, ClipboardList,
  ArrowRight, Zap, Globe, Award, ChevronDown, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';
import { ServiceCard } from '../components/shared/ServiceCard';
import { ClientLogos } from '../components/shared/ClientLogos';
import { StatsBar } from '../components/shared/Stats';
import { SEO, pageSEO } from '../components/shared/SEO';

// Guidewire Ecosystem Card Component
const EcosystemCard = ({ icon: Icon, label, delay, position, glowColor = 'sky' }) => {
  const glowColors = {
    sky: 'shadow-sky-500/20',
    emerald: 'shadow-emerald-500/20',
    violet: 'shadow-violet-500/20',
    amber: 'shadow-amber-500/20',
  };
  
  return (
    <motion.div
      className={`absolute ${position}`}
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <motion.div
        animate={{ 
          y: [0, -8, 0],
        }}
        transition={{ 
          duration: 4 + delay, 
          repeat: Infinity, 
          ease: "easeInOut",
        }}
      >
        <div className={`
          relative px-5 py-4 rounded-xl 
          bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl 
          border border-white/60 dark:border-slate-700/60
          shadow-lg ${glowColors[glowColor]}
          hover:bg-white/90 dark:hover:bg-slate-800/90 hover:shadow-xl
          transition-all duration-300 cursor-default
          group
        `}>
          {/* Subtle glow behind */}
          <div className={`absolute inset-0 rounded-xl bg-gradient-to-br from-${glowColor}-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
          
          <div className="relative flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br from-sky-50 to-sky-100 dark:from-sky-900/50 dark:to-sky-800/50 flex items-center justify-center`}>
              <Icon className="w-5 h-5 text-sky-600 dark:text-sky-400" />
            </div>
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">
              {label}
            </span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Animated connector line component
const ConnectorLine = ({ d, delay = 0 }) => (
  <motion.path
    d={d}
    fill="none"
    stroke="url(#flowGradient)"
    strokeWidth="1.5"
    strokeLinecap="round"
    initial={{ pathLength: 0, opacity: 0 }}
    animate={{ pathLength: 1, opacity: 1 }}
    transition={{ duration: 1.5, delay, ease: "easeInOut" }}
  />
);

// Data flow particle
const FlowParticle = ({ path, delay = 0, duration = 3 }) => (
  <motion.circle
    r="3"
    fill="#0EA5E9"
    filter="url(#glow)"
    initial={{ opacity: 0 }}
    animate={{ opacity: [0, 1, 1, 0] }}
    transition={{ 
      duration: 0.5, 
      delay,
      repeat: Infinity,
      repeatDelay: duration - 0.5 
    }}
  >
    <animateMotion
      dur={`${duration}s`}
      repeatCount="indefinite"
      begin={`${delay}s`}
      path={path}
    />
  </motion.circle>
);

const services = [
  {
    icon: Shield,
    title: 'PolicyCenter Solutions',
    description: 'End-to-end policy administration implementation with custom product modeling and rating engines.',
    href: '/services/policycenter',
  },
  {
    icon: ClipboardList,
    title: 'ClaimCenter Engineering',
    description: 'Streamline claims processing with intelligent workflows, automation, and third-party integrations.',
    href: '/services/claimcenter',
  },
  {
    icon: CreditCard,
    title: 'BillingCenter Services',
    description: 'Modern billing solutions with payment gateway integrations and flexible installment plans.',
    href: '/services/billingcenter',
  },
  {
    icon: Cloud,
    title: 'Cloud Migration',
    description: 'Seamless migration to Guidewire Cloud with optimized architecture and zero-downtime deployment.',
    href: '/cloud-modernisation',
  },
  {
    icon: TestTube2,
    title: 'QA & Testing',
    description: 'Comprehensive testing services including functional, regression, performance, and security testing.',
    href: '/qa-testing',
  },
  {
    icon: Users,
    title: 'Staffing & Talent',
    description: 'Access certified Guidewire professionals for contract, project, or dedicated team engagements.',
    href: '/staffing',
  },
];

const features = [
  { icon: Zap, text: 'Accelerated Delivery' },
  { icon: Globe, text: 'Global Delivery Centers' },
  { icon: Award, text: '500+ Certified Experts' },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const parallaxY1 = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const parallaxY2 = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const parallaxY3 = useTransform(scrollYProgress, [0, 1], [0, -30]);
  
  return (
    <div data-testid="home-page">
      <SEO {...pageSEO.home} />
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center hero-pattern bg-gradient-to-br from-white via-slate-50 to-sky-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 overflow-hidden"
      >
        {/* Background Elements */}
        <div className="absolute inset-0 bg-grid opacity-40" />
        
        {/* Animated gradient blobs */}
        <motion.div 
          className="absolute top-10 right-[15%] w-[500px] h-[500px] rounded-full opacity-40"
          style={{ 
            background: 'radial-gradient(circle, rgba(14, 165, 233, 0.15) 0%, transparent 70%)',
            y: parallaxY1
          }}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 right-[30%] w-[400px] h-[400px] rounded-full opacity-30"
          style={{ 
            background: 'radial-gradient(circle, rgba(56, 189, 248, 0.2) 0%, transparent 70%)',
            y: parallaxY2
          }}
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <motion.div 
          className="absolute top-[40%] right-[5%] w-[300px] h-[300px] rounded-full opacity-25"
          style={{ 
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
            y: parallaxY3
          }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        
        {/* Shared container for hero content */}
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-4 items-center">
            
            {/* Left column - Text content (7 columns) */}
            <div className="lg:col-span-6 xl:col-span-5 text-left">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-semibold rounded-full mb-6">
                  <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
                  AI-Powered Guidewire Implementation Partner
                </span>
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-6 leading-tight tracking-tight"
                data-testid="hero-title"
              >
                Transforming Insurance Technology with{' '}
                <span className="text-sky-600">AI-Driven Guidewire Solutions</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg lg:text-xl text-slate-600 dark:text-slate-300 mb-8 leading-relaxed"
                data-testid="hero-subtitle"
              >
                End-to-end implementation, intelligent automation, modernization, cloud migration & AI-enhanced staffing solutions powered by certified experts.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-wrap gap-4 mb-10"
              >
                <Link to="/contact">
                  <Button 
                    size="lg"
                    className="bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-600/25 h-14 px-8 text-base"
                    data-testid="hero-contact-btn"
                  >
                    Contact Sales
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-slate-200 hover:bg-slate-50 h-14 px-8 text-base"
                    data-testid="hero-services-btn"
                  >
                    Explore Services
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="lg"
                    variant="ghost"
                    className="text-sky-600 hover:bg-sky-50 h-14 px-8 text-base"
                    data-testid="hero-demo-btn"
                  >
                    Request Demo
                  </Button>
                </Link>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-wrap gap-x-6 gap-y-2"
              >
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <feature.icon className="w-5 h-5 text-sky-500" />
                    <span className="text-sm font-medium">{feature.text}</span>
                  </div>
                ))}
              </motion.div>
            </div>
            
            {/* Right column - Ecosystem visualization (5 columns) */}
            <div className="lg:col-span-6 xl:col-span-7 hidden lg:block relative h-[480px] xl:h-[520px]">
              <div className="absolute inset-0 -right-8 xl:-right-12">
                {/* SVG Connector Lines with Data Flow */}
                <svg 
                  className="absolute inset-0 w-full h-full" 
                  viewBox="0 0 600 550"
                  style={{ zIndex: 0 }}
                >
                  <defs>
                    {/* Gradient for lines */}
                    <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.2" />
                      <stop offset="50%" stopColor="#0EA5E9" stopOpacity="0.5" />
                      <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.2" />
                    </linearGradient>
                    
                    {/* Glow filter for particles */}
                    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                    
                    {/* Gradient for center hub */}
                    <radialGradient id="hubGradient" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.1" />
                      <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                  
                  {/* Central hub glow */}
                  <motion.circle
                    cx="300"
                    cy="275"
                    r="80"
                    fill="url(#hubGradient)"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  />
                  
                  {/* Connection lines - PolicyCenter to Cloud */}
                  <ConnectorLine d="M 300 80 Q 350 150 380 180" delay={0.5} />
                  
                  {/* Connection lines - Cloud to ClaimCenter */}
                  <ConnectorLine d="M 420 220 Q 450 280 420 340" delay={0.7} />
                  
                  {/* Connection lines - ClaimCenter to BillingCenter */}
                  <ConnectorLine d="M 380 380 Q 300 420 220 380" delay={0.9} />
                  
                  {/* Connection lines - BillingCenter to QA */}
                  <ConnectorLine d="M 180 340 Q 120 280 140 220" delay={1.1} />
                  
                  {/* Connection lines - QA to PolicyCenter */}
                  <ConnectorLine d="M 180 180 Q 220 120 270 80" delay={1.3} />
                  
                  {/* Central connecting lines to hub */}
                  <ConnectorLine d="M 300 100 L 300 200" delay={1.5} />
                  <ConnectorLine d="M 400 240 L 340 260" delay={1.6} />
                  <ConnectorLine d="M 400 360 L 340 300" delay={1.7} />
                  <ConnectorLine d="M 200 360 L 260 300" delay={1.8} />
                  <ConnectorLine d="M 160 240 L 260 260" delay={1.9} />
                  
                  {/* Animated data flow particles */}
                  <FlowParticle path="M 300 80 Q 350 150 380 180 L 420 220 Q 450 280 420 340 L 380 380" delay={0} duration={4} />
                  <FlowParticle path="M 380 380 Q 300 420 220 380 L 180 340 Q 120 280 140 220" delay={1.5} duration={4} />
                  <FlowParticle path="M 140 220 L 180 180 Q 220 120 270 80 L 300 80" delay={3} duration={4} />
                  
                  {/* Orbital ring */}
                  <motion.ellipse
                    cx="300"
                    cy="275"
                    rx="180"
                    ry="180"
                    fill="none"
                    stroke="url(#flowGradient)"
                    strokeWidth="1"
                    strokeDasharray="4 8"
                    initial={{ rotate: 0 }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                    style={{ transformOrigin: "300px 275px" }}
                  />
                </svg>
                
                {/* Floating Ecosystem Cards */}
                <motion.div style={{ y: parallaxY1 }}>
                  <EcosystemCard 
                    icon={Shield} 
                    label="PolicyCenter" 
                    delay={0.3}
                    position="top-[20px] left-1/2 -translate-x-1/2"
                    glowColor="sky"
                  />
                </motion.div>
                
                <motion.div style={{ y: parallaxY2 }}>
                  <EcosystemCard 
                    icon={Cloud} 
                    label="Cloud Platform" 
                    delay={0.5}
                    position="top-[150px] right-[30px]"
                    glowColor="emerald"
                  />
                </motion.div>
                
                <motion.div style={{ y: parallaxY3 }}>
                  <EcosystemCard 
                    icon={ClipboardList} 
                    label="ClaimCenter" 
                    delay={0.7}
                    position="top-[300px] right-[50px]"
                    glowColor="sky"
                  />
                </motion.div>
                
                <motion.div style={{ y: parallaxY1 }}>
                  <EcosystemCard 
                    icon={CreditCard} 
                    label="BillingCenter" 
                    delay={0.9}
                    position="top-[310px] left-[70px]"
                    glowColor="violet"
                  />
                </motion.div>
                
                <motion.div style={{ y: parallaxY2 }}>
                  <EcosystemCard 
                    icon={TestTube2} 
                    label="QA & Testing" 
                    delay={1.1}
                    position="top-[160px] left-[25px]"
                    glowColor="amber"
                  />
                </motion.div>
                
                {/* Central Hub Label */}
                <motion.div
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 1.5 }}
                >
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-sky-400/20 blur-xl scale-150" />
                    <div className="relative px-5 py-2.5 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-lg border border-sky-200/50 dark:border-sky-800/50 shadow-lg shadow-sky-500/10">
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-200 whitespace-nowrap">
                        Guidewire Ecosystem
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
            
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <ChevronDown className="w-8 h-8 text-slate-400" />
        </motion.div>
      </section>

      {/* Client Logos */}
      <section className="py-10 bg-white dark:bg-slate-900 border-y border-slate-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ClientLogos />
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 lg:py-16 bg-slate-50 dark:bg-slate-900/50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-semibold rounded-full mb-3">
              Our Services
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-3 tracking-tight">
              Comprehensive Guidewire Solutions
            </h2>
            <p className="text-base lg:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              From implementation to optimization, we deliver end-to-end Guidewire services 
              that accelerate your digital transformation.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {services.map((service, index) => (
              <StaggerItem key={index}>
                <ServiceCard {...service} />
              </StaggerItem>
            ))}
          </StaggerContainer>

          <AnimatedSection className="text-center mt-8" delay={0.4}>
            <Link to="/services">
              <Button 
                variant="outline" 
                size="lg"
                className="border-sky-200 text-sky-600 hover:bg-sky-50"
              >
                View All Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Featured Image Section */}
      <section className="py-12 lg:py-16 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <AnimatedSection>
              <span className="inline-block px-4 py-1.5 bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-semibold rounded-full mb-4">
                Enterprise Solutions
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
                Modern Insurance Platforms Built for Scale
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-5 leading-relaxed">
                Our team of certified experts delivers enterprise-grade Guidewire implementations 
                that transform how insurers operate, innovate, and grow.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: '150+', label: 'Projects Delivered' },
                  { value: '50+', label: 'Enterprise Clients' },
                  { value: '99.9%', label: 'System Uptime' },
                  { value: '40%', label: 'Faster Time-to-Market' },
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800"
                  >
                    <div className="text-2xl font-bold text-sky-600">{stat.value}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
            
            <AnimatedSection direction="right">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-sky-100 to-sky-200 rounded-3xl rotate-2" />
                <div className="relative grid grid-cols-2 gap-3">
                  <motion.img
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop"
                    alt="Analytics Dashboard"
                    className="rounded-2xl shadow-lg w-full h-40 object-cover"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.img
                    src="https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?w=400&h=300&fit=crop"
                    alt="Team Meeting"
                    className="rounded-2xl shadow-lg w-full h-40 object-cover mt-6"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.img
                    src="https://images.unsplash.com/photo-1758518730178-6e237bc8b87d?w=400&h=300&fit=crop"
                    alt="Business Collaboration"
                    className="rounded-2xl shadow-lg w-full h-40 object-cover"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.img
                    src="https://images.unsplash.com/photo-1506399558188-acca6f8cbf41?w=400&h=300&fit=crop"
                    alt="Data Center"
                    className="rounded-2xl shadow-lg w-full h-40 object-cover mt-6"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 lg:py-16 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-3 tracking-tight">
              Delivering Excellence at Scale
            </h2>
            <p className="text-base lg:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Our track record speaks for itself. We've helped dozens of insurers 
              transform their operations with Guidewire.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={0.2}>
            <StatsBar />
          </AnimatedSection>
        </div>
      </section>

      {/* Why Guidepro Section */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-slate-900 to-slate-800 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <AnimatedSection>
              <span className="inline-block px-4 py-1.5 bg-sky-500/20 text-sky-300 text-sm font-semibold rounded-full mb-4">
                Why Guidepro
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 tracking-tight">
                Your Trusted Guidewire Implementation Partner
              </h2>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                We combine deep Guidewire expertise with agile delivery methodologies 
                to accelerate your time-to-market and maximize ROI.
              </p>
              
              <div className="space-y-3">
                {[
                  'API-first architecture for seamless integrations',
                  'Cloud-native solutions optimized for scale',
                  'Agile delivery with continuous improvement',
                  'Dedicated support and managed services',
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-5 h-5 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-slate-200">{item}</span>
                  </motion.div>
                ))}
              </div>
              
              <Link to="/about" className="inline-block mt-6">
                <Button 
                  size="lg"
                  className="bg-sky-500 hover:bg-sky-600 text-white"
                >
                  Learn More About Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </AnimatedSection>
            
            <AnimatedSection direction="right" className="relative">
              <div className="relative">
                <div className="absolute -inset-4 bg-sky-500/20 rounded-3xl blur-2xl" />
                <div className="relative overflow-hidden rounded-2xl">
                  <motion.img
                    src="https://images.unsplash.com/photo-1758518729685-f88df7890776?w=800&h=600&fit=crop"
                    alt="Professional Team Collaboration"
                    className="w-full h-auto rounded-2xl"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.5 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
                  
                  {/* Stats overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="grid grid-cols-4 gap-4">
                      {[
                        { value: '98%', label: 'Satisfaction' },
                        { value: '40%', label: 'Faster' },
                        { value: '60%', label: 'Cost Saved' },
                        { value: '24/7', label: 'Support' },
                      ].map((stat, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          className="text-center"
                        >
                          <div className="text-2xl font-bold text-white">
                            {stat.value}
                          </div>
                          <div className="text-xs text-slate-300">
                            {stat.label}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Case Studies Preview with Images */}
      <section className="py-12 lg:py-16 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-semibold rounded-full mb-3">
              Success Stories
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-3 tracking-tight">
              Transforming Insurance Leaders
            </h2>
            <p className="text-base lg:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              See how we've helped leading insurers modernize their operations and achieve 
              breakthrough results with Guidewire.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&h=400&fit=crop",
                title: "Personal Lines Transformation",
                client: "Regional Insurance Leader",
                result: "60% faster quote-to-bind",
                category: "PolicyCenter"
              },
              {
                image: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&h=400&fit=crop",
                title: "Commercial Fleet Platform",
                client: "National Fleet Insurer",
                result: "40% reduction in claims",
                category: "ClaimCenter"
              },
              {
                image: "https://images.unsplash.com/photo-1560472355-536de3962603?w=600&h=400&fit=crop",
                title: "Cloud Migration Success",
                client: "Global Specialty Insurer",
                result: "50% cost reduction",
                category: "Cloud"
              }
            ].map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <Link to="/case-studies">
                  <div className="relative overflow-hidden rounded-2xl mb-3">
                    <motion.img
                      src={study.image}
                      alt={study.title}
                      className="w-full h-52 object-cover"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.5 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-sky-500 text-white text-xs font-semibold rounded-full">
                        {study.category}
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="text-white/80 text-xs mb-1">{study.client}</p>
                      <h4 className="text-white font-bold">{study.title}</h4>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold text-sky-600">{study.result.split(' ')[0]}</span>
                      <span className="text-slate-600 dark:text-slate-400 text-sm">{study.result.split(' ').slice(1).join(' ')}</span>
                    </div>
                    <ArrowRight className="w-5 h-5 text-sky-600 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <AnimatedSection className="text-center mt-8">
            <Link to="/case-studies">
              <Button 
                variant="outline" 
                size="lg"
                className="border-sky-200 text-sky-600 hover:bg-sky-50"
              >
                View All Case Studies
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-sky-50 to-white dark:from-slate-800 dark:to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
              Ready to Transform Your Insurance Operations?
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto mb-6">
              Let's discuss how Guidepro can help you achieve your Guidewire implementation goals.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-600/25 h-12 px-6"
                  data-testid="cta-contact-btn"
                >
                  Schedule a Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-slate-200 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 h-12 px-6"
                >
                  View Case Studies
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Home;
